// Approximate latitude/longitude for IANA timezone identifiers, so the
// observer HUD and initial camera frame land on the *city*, not just the
// timezone's central meridian. Covers common zones; uncovered zones fall
// back to a meridian estimate from the UTC offset.

const ZONE_COORDS: Record<string, [number, number]> = {
  // ---- Europe ----
  "Europe/London":      [51.51,   -0.13],
  "Europe/Dublin":      [53.35,   -6.26],
  "Europe/Lisbon":      [38.72,   -9.14],
  "Europe/Madrid":      [40.42,   -3.70],
  "Europe/Paris":       [48.86,    2.35],
  "Europe/Amsterdam":   [52.37,    4.90],
  "Europe/Brussels":    [50.85,    4.35],
  "Europe/Berlin":      [52.52,   13.40],
  "Europe/Zurich":      [47.37,    8.54],
  "Europe/Rome":        [41.90,   12.49],
  "Europe/Vienna":      [48.21,   16.37],
  "Europe/Prague":      [50.08,   14.44],
  "Europe/Warsaw":      [52.23,   21.01],
  "Europe/Vilnius":     [54.69,   25.28],
  "Europe/Riga":        [56.95,   24.11],
  "Europe/Tallinn":     [59.44,   24.75],
  "Europe/Helsinki":    [60.17,   24.94],
  "Europe/Stockholm":   [59.33,   18.07],
  "Europe/Oslo":        [59.91,   10.75],
  "Europe/Copenhagen":  [55.68,   12.57],
  "Europe/Athens":      [37.98,   23.73],
  "Europe/Istanbul":    [41.01,   28.98],
  "Europe/Kiev":        [50.45,   30.52],
  "Europe/Kyiv":        [50.45,   30.52],
  "Europe/Moscow":      [55.75,   37.62],
  "Europe/Bucharest":   [44.43,   26.10],
  "Europe/Sofia":       [42.70,   23.32],
  "Europe/Budapest":    [47.50,   19.04],
  "Europe/Belgrade":    [44.79,   20.45],

  // ---- Americas ----
  "America/New_York":     [40.71,  -74.01],
  "America/Toronto":      [43.65,  -79.38],
  "America/Chicago":      [41.88,  -87.63],
  "America/Denver":       [39.74, -104.99],
  "America/Phoenix":      [33.45, -112.07],
  "America/Los_Angeles":  [34.05, -118.24],
  "America/Vancouver":    [49.28, -123.12],
  "America/Anchorage":    [61.22, -149.90],
  "America/Halifax":      [44.65,  -63.58],
  "America/Mexico_City":  [19.43,  -99.13],
  "America/Havana":       [23.13,  -82.38],
  "America/Bogota":       [ 4.71,  -74.07],
  "America/Lima":         [-12.05, -77.04],
  "America/Santiago":     [-33.45, -70.67],
  "America/Buenos_Aires": [-34.60, -58.38],
  "America/Sao_Paulo":    [-23.55, -46.63],
  "America/Caracas":      [10.48,  -66.90],
  "America/Recife":       [ -8.05, -34.88],

  // ---- Africa / Middle East ----
  "Africa/Lagos":       [ 6.45,    3.40],
  "Africa/Cairo":       [30.04,   31.24],
  "Africa/Nairobi":     [-1.29,   36.82],
  "Africa/Johannesburg":[-26.20,   28.04],
  "Africa/Casablanca":  [33.57,   -7.59],
  "Africa/Accra":       [ 5.60,   -0.19],
  "Africa/Algiers":     [36.73,    3.09],
  "Asia/Jerusalem":     [31.78,   35.22],
  "Asia/Beirut":        [33.89,   35.50],
  "Asia/Riyadh":        [24.71,   46.68],
  "Asia/Tehran":        [35.69,   51.39],
  "Asia/Dubai":         [25.20,   55.27],
  "Asia/Baghdad":       [33.31,   44.37],
  "Asia/Qatar":         [25.29,   51.53],

  // ---- Asia ----
  "Asia/Kolkata":       [19.08,   72.88],
  "Asia/Karachi":       [24.86,   67.00],
  "Asia/Dhaka":         [23.81,   90.41],
  "Asia/Kathmandu":     [27.72,   85.32],
  "Asia/Bangkok":       [13.76,  100.50],
  "Asia/Jakarta":       [-6.21,  106.85],
  "Asia/Singapore":     [ 1.35,  103.82],
  "Asia/Kuala_Lumpur":  [ 3.14,  101.69],
  "Asia/Manila":        [14.60,  120.98],
  "Asia/Hong_Kong":     [22.32,  114.17],
  "Asia/Taipei":        [25.03,  121.57],
  "Asia/Shanghai":      [31.23,  121.47],
  "Asia/Seoul":         [37.57,  126.98],
  "Asia/Tokyo":         [35.68,  139.65],
  "Asia/Ulaanbaatar":   [47.89,  106.91],
  "Asia/Almaty":        [43.24,   76.95],
  "Asia/Tashkent":      [41.31,   69.28],

  // ---- Oceania ----
  "Australia/Perth":     [-31.95, 115.86],
  "Australia/Adelaide":  [-34.93, 138.60],
  "Australia/Sydney":    [-33.87, 151.21],
  "Australia/Melbourne": [-37.81, 144.96],
  "Australia/Brisbane":  [-27.47, 153.03],
  "Pacific/Auckland":    [-36.85, 174.77],
  "Pacific/Honolulu":    [ 21.31,-157.86],
  "Pacific/Fiji":        [-18.14, 178.44],

  // ---- Misc ----
  "Etc/UTC":            [ 0.00,   0.00],
  "UTC":                [ 0.00,   0.00],
};

export interface ObserverLocation {
  lat: number;
  lng: number;
  zone: string;
  /** True when we resolved the city from the timezone map; false if we
   *  had to fall back to a meridian estimate. */
  isCity: boolean;
}

export function resolveObserverLocation(): ObserverLocation {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
  const hit = ZONE_COORDS[zone];
  if (hit) {
    return { lat: hit[0], lng: hit[1], zone, isCity: true };
  }
  const offsetHours = -new Date().getTimezoneOffset() / 60;
  return { lat: 30, lng: offsetHours * 15, zone, isCity: false };
}
